import { Fragment, useMemo, useState } from 'react';
import type { ReactElement } from 'react';

import { terminal } from '../app/terminal.js';
import { useApp } from '@weke/ui';
import {
  formatPrice,
  formatSignedMoney,
  formatVolume,
  priceDigits,
  parsePriceInput,
} from '@weke/ui';
import { explainRejection, ORDER_STATE_LABEL } from '@weke/ui';
import { sortedPositions, valuedPositions, workingOrders } from '@weke/ui';
import type { PositionRow, OrderRow } from '@weke/ui';
import type { PreviewSymbol } from '@weke/protocol';
import { Panel } from '@weke/ui';

type Tab = 'positions' | 'orders';

/**
 * Positions and working orders.
 *
 * ## Hedging, not netting
 *
 * Two buys on EURUSD are **two rows** (doc 02 §4.1). The terminal never merges
 * them, never shows a net volume and never averages their prices: a trader with
 * two hedged positions has two independent stops and two independent closes, and
 * a UI that netted them would be lying about which one just closed.
 *
 * ## One source: the account snapshot
 *
 * `Now` and `P&L` are two readings of **one** server evaluation. The snapshot
 * carries `mark_e8` (the close-side mark `floatingPnl` used) beside
 * `floating_minor`; `Now` renders the mark and `P&L` renders the amount, both
 * from the same entry. A quote arriving between polls therefore moves neither
 * cell, and the pair goes stale together — which the status bar's `marks`
 * clock makes visible. Reading `Now` from the live quote stream was the defect:
 * at a streamed 1.06400 the row showed the snapshot's −21.00, which is the P&L
 * of an older mark.
 *
 * ## Closing
 *
 * "Close" sends `CLOSE_POSITION` **without a volume**, so the server closes what
 * is actually there. "Half" sends half of the volume the server last reported,
 * snapped down to the instrument's step; if half is below the minimum, the
 * button is disabled rather than silently becoming a full close.
 */
export function PositionsPanel(props: { readonly initialTab?: Tab; readonly hideTabs?: boolean } = {}): ReactElement {
  const app = useApp();
  const [tab, setTab] = useState<Tab>(props.initialTab ?? 'positions');
  const [editing, setEditing] = useState<string | null>(null);
  const [draftSl, setDraftSl] = useState('');
  const [draftTp, setDraftTp] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const symbolById = useMemo(() => {
    const map = new Map<string, PreviewSymbol>();
    for (const symbol of app.symbols) map.set(symbol.id, symbol);
    return map;
  }, [app.symbols]);
  const positions = sortedPositions(app.book);
  const orders = workingOrders(app.book);
  const valued = valuedPositions(app.account);
  const digits = app.account?.digits ?? 2;

  const valuationOf = (positionId: string): { floatingMinor: string; markE8: string } | null =>
    valued[positionId] ?? null;

  const run = async (key: string, action: () => Promise<void>): Promise<void> => {
    setBusy(key);
    setMessage(null);
    try {
      await action();
    } finally {
      setBusy(null);
    }
  };

  const close = (row: PositionRow, volumeE4?: string): void => {
    void run(`close-${row.positionId}-${volumeE4 ?? 'all'}`, async () => {
      try {
        const response = await terminal.closePosition(row.positionId, volumeE4);
        terminal.reportResponse(response, volumeE4 === undefined ? 'Position closed' : 'Position reduced');
        if (response.kind === 'REJECTED') setMessage(explainRejection(response.code).title);
        void terminal.pollSnapshot();
        void terminal.refreshDeals();
      } catch (error) {
        setMessage((error as Error).message);
      }
    });
  };

  const halfVolume = (row: PositionRow, symbol: PreviewSymbol): string | null => {
    const step = BigInt(symbol.volume_step_e4);
    const half = (BigInt(row.volume) / step / 2n) * step;
    if (half < BigInt(symbol.volume_min_e4)) return null;
    return half.toString();
  };

  const beginEdit = (row: PositionRow): void => {
    const symbol = symbolById.get(row.symbolId);
    if (symbol === undefined) return;
    setEditing(row.positionId);
    setDraftSl(row.sl === null ? '' : formatPrice(row.sl, priceDigits(symbol.digits, symbol.tick_size_e8)));
    setDraftTp(row.tp === null ? '' : formatPrice(row.tp, priceDigits(symbol.digits, symbol.tick_size_e8)));
    setMessage(null);
  };

  /**
   * Apply SL/TP. `undefined` keeps a field, `null` clears it — the difference
   * docs/10 §10 warns about. A field the trader did not change is omitted.
   */
  const applyEdit = (row: PositionRow): void => {
    const symbol = symbolById.get(row.symbolId);
    if (symbol === undefined) return;
    const slText = draftSl.trim();
    const tpText = draftTp.trim();
    const currentSl = row.sl === null ? '' : formatPrice(row.sl, priceDigits(symbol.digits, symbol.tick_size_e8));
    const currentTp = row.tp === null ? '' : formatPrice(row.tp, priceDigits(symbol.digits, symbol.tick_size_e8));

    let sl: string | null | undefined;
    if (slText !== currentSl) {
      if (slText.length === 0) sl = null;
      else {
        const parsed = parsePriceInput(slText, symbol.digits, symbol.tick_size_e8, 'Stop loss');
        if (!parsed.ok) {
          setMessage(parsed.message);
          return;
        }
        sl = parsed.value;
      }
    }
    let tp: string | null | undefined;
    if (tpText !== currentTp) {
      if (tpText.length === 0) tp = null;
      else {
        const parsed = parsePriceInput(tpText, symbol.digits, symbol.tick_size_e8, 'Take profit');
        if (!parsed.ok) {
          setMessage(parsed.message);
          return;
        }
        tp = parsed.value;
      }
    }
    if (sl === undefined && tp === undefined) {
      setEditing(null);
      setMessage('No change.');
      return;
    }
    void run(`edit-${row.positionId}`, async () => {
      try {
        const response = await terminal.modifyPosition(row.positionId, sl, tp);
        terminal.reportResponse(response, 'Stop levels updated');
        if (response.kind === 'REJECTED') setMessage(explainRejection(response.code).title);
        else setEditing(null);
        void terminal.pollSnapshot();
      } catch (error) {
        setMessage((error as Error).message);
      }
    });
  };

  const cancel = (order: OrderRow): void => {
    void run(`cancel-${order.orderId}`, async () => {
      try {
        const response = await terminal.cancelOrder(order.orderId);
        terminal.reportResponse(response, 'Order cancelled');
        if (response.kind === 'REJECTED') setMessage(explainRejection(response.code).title);
      } catch (error) {
        setMessage((error as Error).message);
      }
    });
  };

  return (
    <Panel
      id="positions"
      title="Positions"
      badge={`${positions.length} open · ${orders.length} working`}
      actions={
        <span className="flex items-center gap-1">
          {props.hideTabs === true ? null : (
            <>
              <button
                type="button"
                className={tab === 'positions' ? 'chip chip-on cursor-pointer' : 'chip cursor-pointer'}
                aria-pressed={tab === 'positions'}
                onClick={() => setTab('positions')}
              >
                Positions
              </button>
              <button
                type="button"
                className={tab === 'orders' ? 'chip chip-on cursor-pointer' : 'chip cursor-pointer'}
                aria-pressed={tab === 'orders'}
                onClick={() => setTab('orders')}
              >
                Orders
              </button>
            </>
          )}
          <button
            type="button"
            className="btn h-5"
            disabled={positions.length === 0 || busy !== null}
            title="Close every open position (one command each)"
            onClick={() =>
              void run('close-all', async () => {
                const count = await terminal.closeAll(positions.map((row) => row.positionId));
                setMessage(
                  count === positions.length
                    ? `Closed ${count} position${count === 1 ? '' : 's'}.`
                    : `Closed ${count} of ${positions.length}; see the messages above.`,
                );
                void terminal.pollSnapshot();
                void terminal.refreshDeals();
              })
            }
            data-testid="close-all"
          >
            Close all
          </button>
        </span>
      }
    >
      {message === null ? null : (
        <p className="muted border-b border-line px-2 py-1 text-2xs" role="status">
          {message}
        </p>
      )}
      {tab === 'positions' ? (
        <table className="data-table" aria-label="Open positions">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Side</th>
              <th>Volume</th>
              <th>Open</th>
              <th>Now</th>
              <th>SL</th>
              <th>TP</th>
              <th>P&amp;L</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {positions.length === 0 ? (
              <tr>
                <td colSpan={9} className="muted py-3 text-center">
                  No open positions. Place an order from the ticket.
                </td>
              </tr>
            ) : null}
            {positions.map((row) => {
              const symbol = symbolById.get(row.symbolId);
              if (symbol === undefined) return null;
              const valuation = valuationOf(row.positionId);
              const floatingMinor = valuation?.floatingMinor ?? null;
              // Both cells come from the snapshot entry above: never from the
              // quote stream (see the header comment).
              const now = valuation?.markE8 ?? null;
              const rowDigits = priceDigits(symbol.digits, symbol.tick_size_e8);
              const half = halfVolume(row, symbol);
              const isEditing = editing === row.positionId;
              const negative = floatingMinor !== null && floatingMinor.startsWith('-');
              return (
                <Fragment key={row.positionId}>
                  <tr data-testid={`position-${row.positionId}`}>
                    <td className="font-medium">{symbol.name}</td>
                    <td className={row.side === 'BUY' ? 'buy' : 'sell'}>
                      {row.side === 'BUY' ? 'Buy ↑' : 'Sell ↓'}
                    </td>
                    <td className="num" data-testid="position-volume">
                      {formatVolume(row.volume, symbol.volume_step_e4)}
                    </td>
                    <td className="num" data-testid="position-open">
                      {formatPrice(row.openPrice, rowDigits)}
                    </td>
                    <td className="num" data-testid="position-now">
                      {now === null ? '—' : formatPrice(now, rowDigits)}
                    </td>
                    <td className="num" data-testid="position-sl">
                      {formatPrice(row.sl, rowDigits)}
                    </td>
                    <td className="num" data-testid="position-tp">
                      {formatPrice(row.tp, rowDigits)}
                    </td>
                    <td
                      className={`num ${floatingMinor === null ? 'faint' : negative ? 'loss' : floatingMinor === '0' ? 'muted' : 'profit'}`}
                      data-testid="position-pnl"
                    >
                      {floatingMinor === null ? '—' : formatSignedMoney(floatingMinor, digits)}
                    </td>
                    <td>
                      <span className="flex justify-end gap-1">
                        <button
                          type="button"
                          className="btn h-5 px-1"
                          onClick={() => (isEditing ? setEditing(null) : beginEdit(row))}
                          aria-expanded={isEditing}
                          title="Edit stop loss and take profit"
                        >
                          SL/TP
                        </button>
                        <button
                          type="button"
                          className="btn h-5 px-1"
                          disabled={half === null || busy !== null}
                          title={
                            half === null
                              ? 'Half of this position is below the minimum volume'
                              : 'Close half of this position'
                          }
                          onClick={() => {
                            if (half !== null) close(row, half);
                          }}
                          data-testid="close-half"
                        >
                          ½
                        </button>
                        <button
                          type="button"
                          className="btn h-5 px-1"
                          disabled={busy !== null}
                          onClick={() => close(row)}
                          data-testid="close-position"
                        >
                          Close
                        </button>
                      </span>
                    </td>
                  </tr>
                  {isEditing ? (
                    <tr key={`${row.positionId}-edit`}>
                      <td colSpan={9} className="bg-surface-2">
                        <div className="flex flex-wrap items-center gap-2 px-1 py-1">
                          <label className="flex items-center gap-1">
                            <span className="label">SL</span>
                            <input
                              className="field w-24"
                              value={draftSl}
                              onChange={(event) => setDraftSl(event.target.value)}
                              inputMode="decimal"
                              aria-label={`Stop loss for ${symbol.name}`}
                              autoFocus
                            />
                          </label>
                          <label className="flex items-center gap-1">
                            <span className="label">TP</span>
                            <input
                              className="field w-24"
                              value={draftTp}
                              onChange={(event) => setDraftTp(event.target.value)}
                              inputMode="decimal"
                              aria-label={`Take profit for ${symbol.name}`}
                            />
                          </label>
                          <button
                            type="button"
                            className="btn btn-primary h-5"
                            disabled={busy !== null}
                            onClick={() => applyEdit(row)}
                          >
                            Apply
                          </button>
                          <button type="button" className="btn h-5" onClick={() => setEditing(null)}>
                            Cancel
                          </button>
                          <span className="faint text-2xs">
                            Leave a field as it is to keep it; clear it to remove that level.
                          </span>
                          {message === null ? null : <span className="text-2xs text-sell">{message}</span>}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      ) : (
        <table className="data-table" aria-label="Working orders">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Side</th>
              <th>Type</th>
              <th>Volume</th>
              <th>Filled</th>
              <th>Limit</th>
              <th>Stop</th>
              <th>State</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={9} className="muted py-3 text-center">
                  No working orders.
                </td>
              </tr>
            ) : null}
            {orders.map((order) => {
              const symbol = symbolById.get(order.symbolId);
              if (symbol === undefined) return null;
              return (
                <tr key={order.orderId} data-testid={`order-${order.orderId}`}>
                  <td className="font-medium">{symbol.name}</td>
                  <td className={order.side === 'BUY' ? 'buy' : 'sell'}>
                    {order.side === 'BUY' ? 'Buy ↑' : 'Sell ↓'}
                  </td>
                  <td>{order.type.replace('_', ' ').toLowerCase()}</td>
                  <td className="num">{formatVolume(order.volume, symbol.volume_step_e4)}</td>
                  <td className="num">{formatVolume(order.filledVolume, symbol.volume_step_e4)}</td>
                  <td className="num">{formatPrice(order.limitPrice, symbol.digits)}</td>
                  <td className="num">{formatPrice(order.stopPrice, symbol.digits)}</td>
                  <td className="faint">{ORDER_STATE_LABEL[order.state] ?? order.state}</td>
                  <td>
                    <span className="flex justify-end">
                      <button
                        type="button"
                        className="btn h-5 px-1"
                        disabled={busy !== null}
                        onClick={() => cancel(order)}
                        data-testid="cancel-order"
                      >
                        Cancel
                      </button>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
}



